import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  Popconfirm,
  Result,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

import {
  activateAssessmentScenario,
  cloneAssessmentScenario,
  deleteAssessmentScenario,
  fetchAssessmentContent,
  listAssessmentLinks,
  updateAssessmentScenario,
  validateAssessmentScenario,
} from './api';
import DefinitionEditor from './DefinitionEditor';
import ImportExport from './ImportExport';
import SessionsResults from './SessionsResults';
import {
  formatDateTime,
  JsonBlock,
  parseJsonObject,
  ScenarioStatusTag,
  ValidationPanel,
} from './shared';
import {
  AssessmentContent,
  AssessmentSessionLink,
  AssessmentValidation,
} from './types';

type Props = {
  code: string;
  onBack: () => void;
  onOpen: (code: string) => void;
};

const AssessmentWorkspace: React.FC<Props> = ({ code, onBack, onOpen }) => {
  const [content, setContent] = useState<AssessmentContent | null>(null);
  const [links, setLinks] = useState<AssessmentSessionLink[]>([]);
  const [validation, setValidation] = useState<AssessmentValidation | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [validationLoading, setValidationLoading] = useState(false);
  const [error, setError] = useState('');
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [metadataForm] = Form.useForm();
  const [cloneForm] = Form.useForm();

  const loadLinks = useCallback(async () => {
    setLinks(await listAssessmentLinks(code));
  }, [code]);

  const loadValidation = useCallback(async () => {
    setValidationLoading(true);
    try {
      setValidation(await validateAssessmentScenario(code));
    } finally {
      setValidationLoading(false);
    }
  }, [code]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [nextContent, nextValidation, nextLinks] = await Promise.all([
        fetchAssessmentContent(code),
        validateAssessmentScenario(code),
        listAssessmentLinks(code),
      ]);
      setContent(nextContent);
      setValidation(nextValidation);
      setLinks(nextLinks);
    } catch (requestError) {
      setError((requestError as Error).message);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    void load();
  }, [load]);

  const openMetadata = () => {
    if (!content) return;
    metadataForm.setFieldsValue({
      name: content.scenario.name,
      model_version: content.scenario.model_version,
      params: JSON.stringify(content.scenario.params || {}, null, 2),
    });
    setMetadataOpen(true);
  };

  const saveMetadata = async () => {
    if (!content) return;
    try {
      const values = await metadataForm.validateFields();
      setSaving(true);
      await updateAssessmentScenario(code, {
        name: values.name.trim(),
        model_version: values.model_version.trim(),
        params: parseJsonObject(values.params, 'Parameters'),
      });
      message.success('Draft metadata updated.');
      setMetadataOpen(false);
      await load();
    } catch (requestError) {
      if ((requestError as any)?.errorFields) return;
      message.error((requestError as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const openClone = () => {
    if (!content) return;
    cloneForm.setFieldsValue({
      code: `${code}-copy`,
      name: `${content.scenario.name} (copy)`,
      model_version: content.scenario.model_version,
    });
    setCloneOpen(true);
  };

  const clone = async () => {
    try {
      const values = await cloneForm.validateFields();
      setSaving(true);
      const created = await cloneAssessmentScenario(code, {
        code: values.code.trim(),
        name: values.name.trim(),
        model_version: values.model_version.trim(),
      });
      message.success(`Created definition-only draft ${created.code}.`);
      setCloneOpen(false);
      onOpen(created.code);
    } catch (requestError) {
      if ((requestError as any)?.errorFields) return;
      message.error((requestError as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const activate = () => {
    if (!content || !validation) return;
    Modal.confirm({
      title: `Activate ${code}?`,
      content: (
        <div className="assessment-detail-stack">
          <Alert
            showIcon
            type={validation.can_activate ? 'warning' : 'error'}
            title={
              validation.can_activate
                ? 'Activation makes this definition read-only'
                : 'The backend does not currently allow activation'
            }
            description="The backend reruns the unchanged runtime preflight under a database lock. This UI cannot deactivate or edit an active definition; future changes require a definition-only clone."
          />
          <ValidationPanel validation={validation} />
        </div>
      ),
      okText: 'Activate and lock',
      okButtonProps: { disabled: !validation.can_activate },
      width: 720,
      onOk: async () => {
        try {
          await activateAssessmentScenario(code);
          message.success(`${code} is active and read-only.`);
          await load();
        } catch (requestError) {
          message.error((requestError as Error).message);
          throw requestError;
        }
      },
    });
  };

  const remove = async () => {
    try {
      await deleteAssessmentScenario(code);
      message.success(`Deleted unlinked draft ${code}.`);
      onBack();
    } catch (requestError) {
      message.error((requestError as Error).message);
    }
  };

  if (loading && !content) {
    return (
      <div className="assessment-loading">
        <Spin size="large" tip="Loading assessment definition" />
      </div>
    );
  }

  if (!content) {
    return (
      <Result
        status="error"
        title="Assessment could not be loaded"
        subTitle={error}
        extra={<Button onClick={onBack}>Back to catalog</Button>}
      />
    );
  }

  const { scenario } = content;

  const overview = (
    <div className="assessment-detail-stack">
      <Card
        size="small"
        title="Definition identity"
        extra={
          <Button
            icon={<EditOutlined />}
            disabled={!scenario.editable}
            onClick={openMetadata}
          >
            Edit draft metadata
          </Button>
        }
      >
        <Descriptions bordered size="small" column={3}>
          <Descriptions.Item label="Stable code">
            <Typography.Text code>{scenario.code}</Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Lifecycle">
            <ScenarioStatusTag
              label={scenario.status_label}
              editable={scenario.editable}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Model version">
            {scenario.model_version}
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {formatDateTime(scenario.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label="Updated">
            {formatDateTime(scenario.updated_at)}
          </Descriptions.Item>
          <Descriptions.Item label="Management mode">
            <Tag color={scenario.editable ? 'blue' : 'default'}>
              {scenario.editable ? 'EDITABLE DRAFT' : 'READ ONLY'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Instruments">
            {scenario.instrument_count}
          </Descriptions.Item>
          <Descriptions.Item label="Periods">
            {scenario.period_count}
          </Descriptions.Item>
          <Descriptions.Item label="Benchmark rows">
            {scenario.benchmark_count}
          </Descriptions.Item>
          <Descriptions.Item label="News score rows">
            {scenario.news_score_count}
          </Descriptions.Item>
          <Descriptions.Item label="Linked sessions">
            {scenario.linked_session_count}
          </Descriptions.Item>
          <Descriptions.Item label="Candidate results">
            {scenario.result_count}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Card size="small" title="Stored scenario parameters">
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="Stored contract data"
          description="This surface displays and, for an editable draft, persists the existing params JSON. It does not reinterpret parameters or introduce a separate scoring configuration. Runtime diagnostics warn when current adapters ignore a stored override."
        />
        <JsonBlock value={scenario.params} />
      </Card>
      <Card
        size="small"
        title="Runtime-equivalent validation"
        extra={
          <Button
            icon={<ReloadOutlined />}
            loading={validationLoading}
            onClick={() =>
              loadValidation().catch(requestError =>
                message.error(requestError.message),
              )
            }
          >
            Revalidate
          </Button>
        }
      >
        <ValidationPanel validation={validation} loading={validationLoading} />
      </Card>
    </div>
  );

  return (
    <div>
      <div className="assessment-workspace-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            Catalog
          </Button>
          <div>
            <Typography.Title level={3} className="assessment-title">
              {scenario.name}
            </Typography.Title>
            <Space>
              <Typography.Text code>{scenario.code}</Typography.Text>
              <ScenarioStatusTag
                label={scenario.status_label}
                editable={scenario.editable}
              />
              <Typography.Text type="secondary">
                {scenario.model_version}
              </Typography.Text>
            </Space>
          </div>
        </Space>
        <Space wrap>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
            Refresh
          </Button>
          <Button icon={<CopyOutlined />} onClick={openClone}>
            Clone definition
          </Button>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            disabled={!validation?.can_activate}
            onClick={activate}
          >
            Activate
          </Button>
          <Popconfirm
            title="Delete this unlinked draft?"
            description="Definition rows will be deleted with it. Linked or active definitions are blocked by the backend."
            disabled={!scenario.editable}
            onConfirm={remove}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={!scenario.editable}
            >
              Delete draft
            </Button>
          </Popconfirm>
        </Space>
      </div>
      {error && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={error}
        />
      )}
      {!scenario.editable && (
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="This definition is locked"
          description={
            scenario.status_label === 'active'
              ? 'Active assessment definitions are immutable in this management surface.'
              : 'This draft has a session link and is immutable in this management surface. Clone it to create a new editable draft.'
          }
        />
      )}
      <Tabs
        defaultActiveKey="overview"
        items={[
          {
            key: 'overview',
            label: 'Overview & validation',
            children: overview,
          },
          {
            key: 'definition',
            label: 'Definition editor',
            children: <DefinitionEditor content={content} onChanged={load} />,
          },
          {
            key: 'sessions',
            label: `Sessions & results (${links.length})`,
            children: (
              <SessionsResults
                scenario={scenario}
                links={links}
                onLinksChanged={async () => {
                  await load();
                }}
              />
            ),
          },
          {
            key: 'import-export',
            label: 'Import & export',
            children: (
              <ImportExport
                scenario={scenario}
                onImported={async createdCode => {
                  if (createdCode) onOpen(createdCode);
                  else await load();
                }}
              />
            ),
          },
        ]}
      />

      <Modal
        title="Edit draft metadata"
        open={metadataOpen}
        onCancel={() => setMetadataOpen(false)}
        onOk={saveMetadata}
        okText="Save draft"
        confirmLoading={saving}
        destroyOnHidden
      >
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="Scenario code is immutable"
          description="Rename or version changes update this unlinked draft only. Clone when you need a new stable identity."
        />
        <Form form={metadataForm} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="model_version"
            label="Model version"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="params" label="Stored parameters (JSON object)">
            <Input.TextArea
              autoSize={{ minRows: 8, maxRows: 22 }}
              spellCheck={false}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Clone ${code}`}
        open={cloneOpen}
        onCancel={() => setCloneOpen(false)}
        onOk={clone}
        okText="Create definition-only clone"
        confirmLoading={saving}
        destroyOnHidden
      >
        <Alert
          className="assessment-callout"
          showIcon
          type="info"
          title="The clone is a new draft"
          description="Only persisted definition data is copied. Session links, captured evidence, task state, and results remain attached to the source."
        />
        <Form form={cloneForm} layout="vertical">
          <Form.Item
            name="code"
            label="New scenario code"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="name" label="New name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="model_version"
            label="Model version"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssessmentWorkspace;
